import { Store } from '@niknevis/store'
import { BootFn, ComponentFn, StoreFn } from '@niknevis/vue-modules/types'
import Vue, { ComponentOptions, VueConstructor } from 'vue'
import { camelize, isPromise } from '@niknevis/vue-modules/utils'
import { container } from 'tsyringe'
import Vuex from 'vuex'

declare module 'vue/types/vue' {
  interface Vue {
    $module: VueModule
  }
}

const providers = Object.create(null)

let vm: Vue

export class VueModule {
  constructor(
    private readonly vue = Vue
  ) {
  }

  readonly name: string = 'VueModule'

  protected imports: typeof VueModule[] = []
  protected components: ComponentFn[] = []
  protected storefiles: StoreFn[] = []
  protected bootfiles: BootFn[] = []
  protected services = Object.create(null)
  protected service = Object.create(null)

  protected app: ComponentOptions<Vue> = {}

  public get appInstance() {
    return vm
  }

  public submodules: Record<string, VueModule> = Object.create(null)
  public exports: Record<string, any> = Object.create(null)
  public root!: VueModule
  public store: Store = new Store()

  public static instance: typeof VueModule

  public static rootModule: VueModule

  private async injectComponents() {
    const instance = this
    for (const compFn of this.components) {
      const { default: comp } = await compFn()
      if (comp.options.methods && 'setModule' in comp.options.methods) {
        const componentInstance = new comp()
        componentInstance.setModule(Object.freeze(instance) as any)
      } else {
        comp.$module = Object.freeze(instance)
      }
    }
  }

  private async loadStore(instance: VueModule) {
    for (const storeFn of this.storefiles) {
      const resultOrPromise: any | Promise<any> = storeFn.call(this, {
        store: this.store,
        root: instance,
        instance: this
      })

      if (isPromise(resultOrPromise)) {
        await resultOrPromise
      }
    }
  }

  private async loadBootFiles(Vue: VueConstructor, instance: VueModule) {
    for (const bootFn of this.bootfiles) {
      const resultOrPromise: any | Promise<any> = bootFn.call(this, {
        app: instance.app,
        Vue,
        exports: this.exports,
        instance: this,
        root: instance
      })

      if (isPromise(resultOrPromise)) {
        await resultOrPromise
      }
    }
  }

  private loadServices(Vue: VueConstructor) {
    Object.keys(this.services).forEach((key) => {
      const serviceClass = this.services[key]

      Object.defineProperty(providers, key, {
        get(): any {
          return container.resolve(serviceClass)
        }
      })

      Object.defineProperty(this.service, key, container.resolve(serviceClass))
    })
  }

  private async resolveDependencies(Vue: VueConstructor, instance: VueModule, resolved: Set<any>, unresolved: Set<any>) {
    const proto = Object.getPrototypeOf(this)
    unresolved.add(proto.constructor)
    for (const dep of this.imports) {
      const vueModule = container.resolve(dep)
      const node = Object.getPrototypeOf(vueModule).constructor

      if (!resolved.has(node)) {
        if (unresolved.has(node)) {
          throw new Error(`circular dependency ${this.name} -> ${vueModule.name}`)
        }
        if (vueModule.name === 'VueModule') {
          throw new Error(`name must be overriden in ${dep.name}`)
        }

        container.register(vueModule.name, {
          useValue: vueModule
        })

        await vueModule.boot(Vue, instance, resolved, unresolved)
      }
      this.submodules[camelize(vueModule.name)] = vueModule
    }
    resolved.add(proto.constructor)
    unresolved.delete(proto.constructor)
  }

  async boot(Vue: VueConstructor, instance: VueModule, resolved: Set<any>, unresolved: Set<any>) {
    this.root = instance
    this.loadServices(Vue)
    this.bootstrap()
    await this.resolveDependencies(Vue, instance, resolved, unresolved)
    await this.loadBootFiles(Vue, instance)
    await this.loadStore(instance)
    await this.injectComponents()
  }

  bootstrap() {
  }

  async install(...args: ConstructorParameters<VueConstructor>) {
    const options = args[0] || {}

    container.register('rootModule', {
      useValue: this
    })

    container.register(this.name, {
      useValue: this
    })

    this.vue.use(Vuex)

    const store = new Vuex.Store({})

    const proto = Object.getPrototypeOf(this)

    const resolved = new Set([proto.constructor])

    const unresolved = new Set()

    await this.boot(this.vue, this, resolved, unresolved)

    const map = new Map()

    /*this.vue.mixin({
      beforeCreate: function initModule() {
        const opts = this.$options as any
        let $module
        if (this.$vnode?.componentOptions?.Ctor) {
          const ctor = this.$vnode.componentOptions.Ctor
          if (map.has(ctor)) {
            $module = map.get(ctor)
          } else if (opts.$module) {
            $module = opts.$module
            map.set(ctor, opts.$module)
          }
        }

        if ($module) {
          this.$module = $module
          this.$store = $module.store
        }

        if (!this.$module && this.$parent?.$module) {
          this.$module = this.$parent.$module
          this.$store = this.$parent.$module.store
        }
      }
    })*/

    // @ts-ignore
    window.__rootModule = this

    VueModule.rootModule = this

    vm = new this.vue({
      // @ts-ignore
      store: store as any,
      ...this.app,
      ...options,
      provide: providers
    })

    return vm
  }
}
